/**
 * ui-renderers/insights.ts
 * Insight/notification card list and unread badge.
 */

import { state } from '../state';
import { dom } from '../dom';
import { escapeHtml, escapeAttr, formatRelativeTime } from './core';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

export const INSIGHT_TYPE_META: Record<string, { icon: string; color: string; pct: number }> = {
  stale_matter:      { icon: '\u23F1', color: '#F0B432', pct: 60 },
  upcoming_deadline: { icon: '\u23F0', color: '#FF5F6A', pct: 85 },
  overdue_invoice:   { icon: '\uD83D\uDCB0', color: '#C9A96E', pct: 45 },
  sentiment_alert:   { icon: '\uD83D\uDCAC', color: '#FF5F6A', pct: 75 },
  billing_anomaly:   { icon: '\uD83D\uDCC8', color: '#C9A96E', pct: 70 },
  daily_briefing:    { icon: '\u2600\uFE0F', color: '#00E5FF', pct: 100 },
};

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

function _buildProgressRing(icon: string, color: string, pct: number): string {
  const r = 16, circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return [
    '<svg class="ins-progress-ring" viewBox="0 0 40 40" style="position:relative;">',
      '<circle class="ring-bg" cx="20" cy="20" r="' + r + '" stroke="rgba(255,255,255,.08)" stroke-width="3" fill="none"/>',
      '<circle class="ring-fill" cx="20" cy="20" r="' + r + '" stroke="' + color + '" stroke-width="3" fill="none"',
        ' stroke-dasharray="' + circ.toFixed(2) + '" stroke-dashoffset="' + offset.toFixed(2) + '"',
        ' stroke-linecap="round" transform="rotate(-90 20 20)"/>',
      '<text x="20" y="24" text-anchor="middle" font-size="13" fill="' + color + '">' + icon + '</text>',
    '</svg>',
  ].join('');
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

export function renderInsightsList(notifications: any[]) {
  const list = dom.insList;
  if (!list) return;

  if (!notifications || notifications.length === 0) {
    list.innerHTML = '<div class="ai-ins-empty">No insights yet.<br>Check back later for updates on your matters.</div>';
    return;
  }

  list.innerHTML = '';
  notifications.forEach(n => {
    const meta    = INSIGHT_TYPE_META[n.type] || INSIGHT_TYPE_META.stale_matter;
    const timeAgo = formatRelativeTime(n.created_at);
    const isUnread = n.is_read === 0;

    const card = document.createElement('div');
    card.className = 'ai-ins-card' + (isUnread ? ' unread' : '');
    card.setAttribute('data-notif-id', escapeAttr(n.id));
    card.setAttribute('data-link',     escapeAttr(n.action_link || ''));
    card.setAttribute('data-title',    escapeAttr(n.title   || ''));
    card.setAttribute('data-message',  escapeAttr(n.message || ''));

    card.innerHTML = [
      _buildProgressRing(meta.icon, meta.color, meta.pct),
      '<div class="ai-ins-card-body">',
        '<div class="ai-ins-title">' + escapeHtml(n.title)   + '</div>',
        '<div class="ai-ins-msg">'   + escapeHtml(n.message) + '</div>',
        '<div class="ai-ins-time">'  + timeAgo               + '</div>',
      '</div>',
      isUnread ? '<div class="ai-ins-unread-dot" aria-label="Unread"></div>' : '',
    ].join('');

    card.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent('akili:insight-click', {
        detail: {
          id:      card.getAttribute('data-notif-id'),
          link:    card.getAttribute('data-link'),
          title:   card.getAttribute('data-title'),
          message: card.getAttribute('data-message'),
        },
      }));
      card.classList.remove('unread');
      const dot = card.querySelector('.ai-ins-unread-dot');
      if (dot && dot.parentNode) dot.parentNode.removeChild(dot);
    });

    list.appendChild(card);
  });
}

export function updateUnreadBadge() {
  const badge    = dom.insBadge;
  const fabBadge = dom.fabBadge;
  const fab      = dom.fab;
  const count    = state.unreadCount;

  if (badge) {
    badge.textContent = count > 9 ? '9+' : String(count);
    if (count > 0) badge.classList.add('visible');
    else           badge.classList.remove('visible');
  }
  if (fab)      fab.classList.toggle('has-unread', count > 0);
  if (fabBadge) fabBadge.textContent = count > 9 ? '9+' : String(count);
}
